import { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { AuthProvider } from "./context/AuthContext";
import Header from "./components/layout/Header";
import MaintenanceBanner from "./components/layout/MaintenanceBanner";
import AuthModal from "./components/auth/AuthModal";
import OAuthRedirectHandler from "./components/auth/OAuthRedirectHandler";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Run from "./pages/Run";
import PastRuns from "./pages/PastRuns";
import RunDetail from "./pages/RunDetail";
import BenchmarkSets from "./pages/BenchmarkSets";
import BenchmarkSetDetail from "./pages/BenchmarkSetDetail";
import GeneratorApiInterface from "./pages/GeneratorApiInterface";
import { buildAuthUrl, sanitizeRedirectPath } from "./utils/authRedirect";
import "./App.css";

export const AppContent = () => {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authMode, setAuthMode] = useState("signin");
  const [authRedirectTo, setAuthRedirectTo] = useState("/");
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get("auth");

    if (mode !== "signin" && mode !== "signup") {
      return;
    }

    setAuthMode(mode);
    setAuthRedirectTo(sanitizeRedirectPath(params.get("redirectTo")));
    setAuthModalOpen(true);
  }, [location.search]);

  const handleAuthClick = (mode, redirectTo) => {
    const sanitizedRedirectTo = sanitizeRedirectPath(redirectTo);
    setAuthMode(mode);
    setAuthRedirectTo(sanitizedRedirectTo);
    setAuthModalOpen(true);
    navigate(
      buildAuthUrl(
        mode,
        sanitizedRedirectTo === "/" ? undefined : sanitizedRedirectTo
      )
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <OAuthRedirectHandler />
      {/* Sticky header area - banner + navigation stay fixed at top */}
      <div className="sticky top-0 z-50">
        <MaintenanceBanner />
        <Header onAuthClick={handleAuthClick} />
      </div>

      <Routes>
        <Route path="/" element={<Landing onAuthClick={handleAuthClick} />} />
        <Route path="/benchmark-sets" element={<BenchmarkSets />} />
        <Route
          path="/generator-api-interface"
          element={<GeneratorApiInterface />}
        />
        <Route
          path="/benchmark-sets/:curriculum/:grade/:subject"
          element={<BenchmarkSetDetail />}
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/run"
          element={
            <ProtectedRoute>
              <Run />
            </ProtectedRoute>
          }
        />
        <Route
          path="/past-runs"
          element={
            <ProtectedRoute>
              <PastRuns />
            </ProtectedRoute>
          }
        />
        <Route
          path="/past-runs/:runId"
          element={
            <ProtectedRoute>
              <RunDetail />
            </ProtectedRoute>
          }
        />
      </Routes>

      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        initialMode={authMode}
        redirectTo={authRedirectTo}
      />
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

export default App;
